import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";

import { fetchWithAuth } from "../../auth/auth";
import { getApiUrl } from "../../utils/getApiUrl";
import { handleGoogleLogin } from "../../pages/login/googleAuth";
import type { RegisterModalModel } from "../../type/register";

import { BaseModal } from "../../components/modalCommon/modalBase";
import { ModalHeader } from "../../components/modalCommon/modalHeader";
import { ModalBody } from "../../components/modalCommon/modalBody";

import "./registerModal.scss";
import { useSpinner } from "../../components/spinner/spinner";

const RegisterModal: React.FC<RegisterModalModel> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false); // <-- new
  const { showSpinner, hideSpinner } = useSpinner();

  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !username || !password) {
      setError("All fields are required.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const body = { email, username, password, idPackage: 1, loginType: 1, avatar: null };
      const resp = await fetchWithAuth(getApiUrl("auth/register"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!resp.ok) {
        const txt = await resp.text();
        try {
          const parsed = JSON.parse(txt);
          setError(parsed.error || `Register failed: ${resp.status}`);
        } catch {
          setError(txt || `Register failed: ${resp.status}`);
        }
        return;
      }

      // Show confirmation message instead of logging in
      setRegistered(true);
    } catch (err: unknown) {
      console.error(err);
      setError(err instanceof Error ? err.message : "Network error");
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (registered) {
    return (
      <BaseModal open={isOpen} onClose={onClose} size="sm">
        <ModalHeader title="Check Your Email" onClose={onClose} />
        <ModalBody>
          <div className="text-center py-3">
            <i className="register-modal-emailicon bi-envelope display-1" aria-hidden="true"></i>
            <p>
              We've sent a confirmation link to <strong>{email}</strong>.
            </p>
            <p className="register-modal-info-2">
              Please check your inbox and click the link to activate your account.
            </p>
            <button
              className="btn btn-success mt-2"
              onClick={() => { onClose(); navigate("/login"); }}
            >
              Go to Login
            </button>
          </div>
        </ModalBody>
      </BaseModal>
    );
  }

  return (
    <BaseModal
      open={isOpen}
      onClose={onClose}
      size="sm"
    >
      <ModalHeader title="Create Account" onClose={onClose} />

      <form onSubmit={handleSubmit}>
        <ModalBody>
          {/* Email */}
          <div className="mb-3">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-control bg-dark text-light border-secondary"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          {/* Username */}
          <div className="mb-3">
            <label className="form-label">Username</label>
            <input
              type="text"
              className="form-control bg-dark text-light border-secondary"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>

          {/* Password */}
          <div className="mb-3">
            <label className="form-label">Password</label>
            <input
              type="password"
              className="form-control bg-dark text-light border-secondary"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {/* Confirm */}
          <div className="mb-3">
            <label className="form-label">Confirm Password</label>
            <input
              type="password"
              className="form-control bg-dark text-light border-secondary"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </div>

          {/* Error */}
          {error && (
            <div className="alert alert-danger py-2 mb-3">
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            className="btn btn-success w-100"
            type="submit"
            disabled={loading}
          >
            {loading ? "Creating account..." : "Register"}
          </button>
        </ModalBody>
      </form>

      <div className="text-center my-3">or</div>

      <div className="d-flex justify-content-center">
        <GoogleLogin
          onSuccess={(res) =>
            handleGoogleLogin(res.credential, navigate, setError, showSpinner, hideSpinner)
          }
          onError={() => setError("Google login failed")}
        />
      </div>

      <div className="text-center mt-3">
        <small>
          Already have an account?{" "}
          <a href="/login" className="text-decoration-none">
            Login here
          </a>
        </small>
      </div>
    </BaseModal>
  );
};

export default RegisterModal;
